import * as HoverCardPrimitive from "@radix-ui/react-hover-card";
import * as React from "react";
import { cx } from "../internal/cx.js";

/**
 * Multi-segment progress bar. Renders one bar broken into proportionally
 * sized colored segments for categorical progress visualizations
 * ("Enrolled / In Progress / Not Started"). Each segment can opt into a
 * Radix HoverCard that opens on hover or keyboard focus to reveal its
 * detail (count + label + breakdown).
 *
 * Different from `<Progress>` (single 0–100% fill) and from a stats row
 * (independent tiles).
 */

export type SegmentedProgressTone =
  | "primary"
  | "success"
  | "warning"
  | "neutral"
  | "danger"
  | "spring"
  | "sunny"
  | "divider";

export type SegmentedProgressSize = "sm" | "md" | "lg";

export interface SegmentedProgressSegment {
  /** Stable id. */
  id: string;
  /** Human-readable category name ("Enrolled", "In Progress"). */
  label: React.ReactNode;
  /**
   * Raw count or quantity for this segment. The component computes the
   * segment width as `value / total`, where total defaults to the sum
   * of all segment values.
   */
  value: number;
  /** Color tone. Default `neutral`. */
  tone?: SegmentedProgressTone;
  /**
   * Optional hover-card content. When provided, the segment becomes a
   * focusable hover trigger that reveals this content on hover/focus.
   */
  hoverContent?: React.ReactNode;
}

export interface SegmentedProgressProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Required accessible label, read aloud as the progressbar's purpose. */
  ariaLabel: string;
  /** Required segment list (1+). */
  segments: SegmentedProgressSegment[];
  /**
   * Optional total denominator. Defaults to the sum of segment values.
   * Pass an explicit total when the segments do not cover the whole
   * population (the remainder is rendered as an empty track).
   */
  total?: number;
  /** Bar height. Default `md`. */
  size?: SegmentedProgressSize;
  /**
   * Show a legend below the bar with one entry per segment.
   * Default `true`.
   */
  showLegend?: boolean;
}

function clampNonNegative(value: number) {
  if (!Number.isFinite(value) || value < 0) return 0;
  return value;
}

export const SegmentedProgress = React.forwardRef<HTMLDivElement, SegmentedProgressProps>(
  function SegmentedProgress(
    { ariaLabel, className, segments, showLegend = true, size = "md", total: totalProp, ...props },
    ref,
  ) {
    const normalizedSegments = segments.map((segment) => ({
      ...segment,
      value: clampNonNegative(segment.value),
      tone: segment.tone ?? "neutral",
    }));
    const sumOfSegments = normalizedSegments.reduce((acc, segment) => acc + segment.value, 0);
    const denominator = Math.max(
      totalProp !== undefined ? clampNonNegative(totalProp) : sumOfSegments,
      sumOfSegments,
    );
    const filledTotal = normalizedSegments.reduce((acc, segment) => acc + segment.value, 0);
    const percentNow = denominator > 0 ? Math.round((filledTotal / denominator) * 100) : 0;

    return (
      <div
        ref={ref}
        className={cx("cw-v2-segmented-progress", `cw-v2-segmented-progress--${size}`, className)}
        {...props}
      >
        <div
          className="cw-v2-segmented-progress__status"
          role="progressbar"
          aria-label={ariaLabel}
          aria-valuemin={0}
          aria-valuemax={denominator || 100}
          aria-valuenow={filledTotal}
          aria-valuetext={
            denominator > 0 ? `${filledTotal} of ${denominator} (${percentNow}%)` : undefined
          }
          tabIndex={0}
        />
        <div
          className="cw-v2-segmented-progress__track"
          aria-hidden={!normalizedSegments.some((segment) => segment.hoverContent)}
        >
          {normalizedSegments.map((segment) => {
            const pct = denominator > 0 ? (segment.value / denominator) * 100 : 0;
            if (pct <= 0) return null;
            const fillProps = {
              className: cx(
                "cw-v2-segmented-progress__fill",
                `cw-v2-segmented-progress__fill--${segment.tone}`,
              ),
              style: { width: `${pct}%` },
            };
            if (!segment.hoverContent) {
              return <div key={segment.id} {...fillProps} aria-hidden />;
            }
            return (
              <HoverCardPrimitive.Root key={segment.id} openDelay={120} closeDelay={120}>
                <HoverCardPrimitive.Trigger asChild>
                  <button
                    type="button"
                    {...fillProps}
                    aria-label={typeof segment.label === "string" ? segment.label : undefined}
                  />
                </HoverCardPrimitive.Trigger>
                <HoverCardPrimitive.Portal>
                  <HoverCardPrimitive.Content
                    align="center"
                    sideOffset={8}
                    className="cw-v2-theme cw-v2-hover-card__content cw-v2-segmented-progress__hover"
                  >
                    {segment.hoverContent}
                  </HoverCardPrimitive.Content>
                </HoverCardPrimitive.Portal>
              </HoverCardPrimitive.Root>
            );
          })}
        </div>
        {showLegend ? (
          <ul className="cw-v2-segmented-progress__legend">
            {normalizedSegments.map((segment) => (
              <li key={segment.id} className="cw-v2-segmented-progress__legend-item">
                <span
                  className={cx(
                    "cw-v2-segmented-progress__swatch",
                    `cw-v2-segmented-progress__swatch--${segment.tone}`,
                  )}
                  aria-hidden
                />
                <span className="cw-v2-segmented-progress__legend-label">{segment.label}</span>
                <span className="cw-v2-segmented-progress__legend-value">{segment.value}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    );
  },
);
SegmentedProgress.displayName = "SegmentedProgress";
